
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/services/supabase/client';
import { Notification, getPaymentStatusText, createNotificationId } from './types';
import { addNotification } from '@/services/notificationService';

// Ограничение времени для платежей, для которых мы показываем уведомления
const PAYMENT_NOTIFICATION_TIME_LIMIT = 48 * 60 * 60 * 1000; // 48 часов

export function usePaymentNotifications(): Notification[] {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { user } = useAuth();
  
  // Используем useEffect вместо хука с возможной циклической зависимостью
  useEffect(() => {
    // Загружаем уведомления о платежах из Supabase
    const loadPaymentNotifications = async () => {
      if (!user) return;

      try {
        // Определяем ID курьера (может совпадать с ID пользователя или определяться по другим полям)
        let courierId = user.id;
        
        // Проверяем, есть ли курьер с таким ID пользователя
        const { data: courierData, error: courierError } = await supabase
          .from('couriers')
          .select('id')
          .or(`id.eq.${user.id},phone.eq.${user?.phone || ''},email.eq.${user?.email || ''}`)
          .single();

        if (courierData) {
          courierId = courierData.id;
        }

        // Получаем только платежи за последние 48 часов
        const timeLimit = new Date();
        timeLimit.setTime(timeLimit.getTime() - PAYMENT_NOTIFICATION_TIME_LIMIT);
        const timeLimitISOString = timeLimit.toISOString();

        // Получаем последние платежи для этого курьера
        const { data: payments, error } = await supabase
          .from('payments')
          .select('*')
          .eq('courier_id', courierId)
          .gt('payment_date', timeLimitISOString) // Фильтруем по времени
          .order('payment_date', { ascending: false })
          .limit(10); // Увеличиваем лимит для большего охвата

        if (error) {
          console.error('Ошибка при загрузке платежей:', error);
          return;
        }

        if (!payments || payments.length === 0) {
          console.log('Платежи не найдены в Supabase');
          return;
        }

        console.log(`Загружено ${payments.length} платежей для уведомлений`);
        
        // Преобразуем платежи в уведомления
        const paymentNotifications: Notification[] = [];
        
        for (const payment of payments) {
          let notificationType: 'payment' | 'system' | 'warning' = 'payment';
          let notificationTitle = 'Информация о платеже';
          
          if (payment.status === 'pending') {
            notificationType = 'warning';
            notificationTitle = 'Ожидается оплата';
          } else if (payment.status === 'error') {
            notificationType = 'warning';
            notificationTitle = 'Ошибка платежа';
          }
          
          // Создаем ID для уведомления о платеже
          const notificationId = `payment-${payment.id}-${payment.status}`;
          
          // Создаем объект уведомления
          const notification: Notification = {
            id: notificationId,
            title: notificationTitle,
            message: `Платеж #${payment.human_number || payment.id} на сумму ${payment.amount} руб. ${getPaymentStatusText(payment.status)}`,
            timestamp: payment.payment_date || new Date().toISOString(),
            read: true, // По умолчанию считаем прочитанными исторические платежи
            type: notificationType
          };
          
          paymentNotifications.push(notification);
          
          // Добавляем в центральную систему уведомлений
          // Для существующих платежей используем showToast: false, чтобы не показывать повторно
          // Для новых платежей (моложе 8 часов) показываем toast
          const paymentTime = new Date(payment.payment_date).getTime();
          const isRecent = (Date.now() - paymentTime) < 8 * 60 * 60 * 1000; // 8 часов
          
          addNotification({
            id: notification.id,
            title: notification.title,
            message: notification.message,
            type: notification.type as 'system' | 'warning' | 'payment',
          }, isRecent);
        }

        setNotifications(paymentNotifications);

      } catch (error) {
        console.error('Ошибка при получении уведомлений о платежах:', error);
      }
    };

    // Загружаем уведомления при монтировании компонента
    loadPaymentNotifications();

  }, [user]);

  return notifications;
}
